import {
  baseUrl as defaultBaseUrl,
  doLogout,
  refreshAccessToken
} from "@/api/config";
import { STATUS_CODE } from "@/constants/errorCode";
import type {
  Dataset,
  DatasetChunkStream,
  DatasetData,
  DatasetFilterPayload,
  DatasetMetadata,
  DatasetMetadataStream,
  H3Configuration
} from "@/types/dataset";
import { buildGridConfig } from "@/utils/dataset";
import { CustomError, UnauthorizedStreamError } from "@/utils/errors";
import { generateDatasetData } from "@/utils/generateDatasetData";

import { fetchEventSource } from "@microsoft/fetch-event-source";

export type StreamDatasetChunksOptions = {
  datasetId: string;
  filterPayload: DatasetFilterPayload[];
  h3Configuration: H3Configuration | null;
  /** Aborting settles the returned promise; it never hangs. */
  signal: AbortSignal;
  baseUrl?: string;
  /**
   * Fires once both the dataset detail and the stream meta have arrived —
   * everything needed to create the layer, before a single row has.
   */
  onMeta?: (meta: DatasetMetadataStream, detail: DatasetMetadata) => void;
  /**
   * Fires per chunk with that chunk's rows and the running total delivered so
   * far — enough to enforce a row budget while the stream is still open.
   */
  onChunk?: (rows: DatasetChunkStream["rows"], delivered: number) => void;
};

export type StreamDatasetChunksResult = {
  datasetMetadata: DatasetMetadata;
  rawDataset: DatasetData;
  dataset: Dataset | undefined;
  /**
   * The filtered row count, as declared by `dataset_data:done` — the only frame
   * worth reading it from, since `dataset_detail` names the dataset's whole size
   * and `dataset_data:meta` is sent before the filters are applied. Falls back
   * to the rows that actually arrived when the frame declares no count.
   */
  totalRows: number;
};

const toAbortError = (signal: AbortSignal): unknown =>
  (signal.reason as unknown) ?? new DOMException("Aborted", "AbortError");

/**
 * Reads the `{ error: { reason, message, details } }` envelope off a failed
 * stream response, so the backend's own reason survives to the caller instead
 * of being flattened into a generic connection failure.
 */
const toStreamError = async (response: Response): Promise<CustomError> => {
  const payload = (await response
    .clone()
    .json()
    .catch(() => null)) as {
    error?: { reason?: string; message?: string; details?: unknown };
  } | null;

  const error = payload?.error;

  return new CustomError(
    error?.message ??
      `Unexpected response status: ${response.status.toString()}`,
    response.status,
    error?.details,
    error?.reason
  );
};

/**
 * Streams one dataset for the Agentic AI workspace, emitting each chunk as it
 * lands so the caller can paint the layer while the rest is still arriving.
 *
 * Separate from `fetchDatasetStream`, which buffers silently, resolves once and
 * cannot report why the backend refused.
 */
const streamOnce = ({
  datasetId,
  filterPayload,
  h3Configuration,
  signal,
  baseUrl,
  onMeta,
  onChunk
}: StreamDatasetChunksOptions): Promise<StreamDatasetChunksResult> =>
  new Promise((resolve, reject) => {
    // An already-aborted signal never fires `abort`, so without this guard the
    // promise would never settle and would stall the SDK's sync chain.
    if (signal.aborted) {
      reject(toAbortError(signal));
      return;
    }

    const deliveredRows: DatasetChunkStream["rows"] = [];
    let datasetMetadata: DatasetMetadata | undefined;
    let datasetMetadataStream: DatasetMetadataStream | undefined;
    let announcedMeta = false;
    let declaredTotalRows: number | undefined;

    const controller = new AbortController();

    signal.addEventListener(
      "abort",
      () => {
        controller.abort();
        // `fetchEventSource` skips `onerror` when its own controller is
        // aborted, so nothing else would ever settle this promise.
        reject(toAbortError(signal));
      },
      { once: true }
    );

    const announceMeta = () => {
      if (announcedMeta) return;
      if (!datasetMetadata || !datasetMetadataStream) return;

      announcedMeta = true;
      onMeta?.(datasetMetadataStream, datasetMetadata);
    };

    fetchEventSource(
      `${baseUrl ?? defaultBaseUrl}/v1/datasets/${datasetId}/data/stream`,
      {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filters: filterPayload,
          ...buildGridConfig(h3Configuration)
        }),
        openWhenHidden: true,

        async onopen(response: Response): Promise<void> {
          if (response.ok) return;

          if (response.status === STATUS_CODE.UNAUTHORIZED) {
            throw new UnauthorizedStreamError();
          }

          throw await toStreamError(response);
        },

        onmessage(msg) {
          const { event, data } = msg;

          if (!data) return;

          switch (event) {
            case "dataset_detail": {
              datasetMetadata = JSON.parse(data);
              announceMeta();
              break;
            }

            case "dataset_data:meta": {
              datasetMetadataStream = JSON.parse(data);
              announceMeta();
              break;
            }

            case "dataset_data:chunk": {
              const rows = JSON.parse(data).rows as DatasetChunkStream["rows"];

              // Avoid spread (`push(...rows)`): passing each row as a separate
              // argument throws "RangeError: Maximum call stack size exceeded"
              // for a large chunk. Loop-push has no such limit.
              for (const row of rows) deliveredRows.push(row);

              onChunk?.(rows, deliveredRows.length);
              break;
            }

            case "dataset_data:done": {
              const total = JSON.parse(data).total_rows;

              if (typeof total === "number") declaredTotalRows = total;
              break;
            }

            case "stream:done": {
              if (!datasetMetadata || !datasetMetadataStream) {
                reject(
                  new CustomError(
                    "The dataset stream finished without describing the dataset.",
                    STATUS_CODE.INTERNAL_SERVER_ERROR
                  )
                );
                controller.abort();
                break;
              }

              const { datasetData, newDataset } = generateDatasetData(
                { dataset_id: datasetId, rows: deliveredRows, seq: 0 },
                datasetMetadataStream,
                true,
                h3Configuration
              );

              resolve({
                datasetMetadata,
                rawDataset: datasetData,
                dataset: newDataset,
                totalRows: declaredTotalRows ?? datasetData.rows.length
              });
              controller.abort();
              break;
            }

            case "stream:error": {
              const payload = JSON.parse(data);

              reject(
                new CustomError(
                  payload.message ?? "The dataset stream failed.",
                  payload.code ?? STATUS_CODE.INTERNAL_SERVER_ERROR,
                  payload.details,
                  payload.reason
                )
              );
              controller.abort();
              break;
            }
          }
        },

        onerror(err) {
          // Reject, then rethrow so `fetchEventSource` disposes instead of
          // retrying on a one-second timer behind our back.
          reject(err);
          throw err;
        }
      }
    );
  });

/**
 * `streamOnce` plus the one retry an expired access token is owed.
 *
 * NOTE: no connection-level retry. A blip fails this dataset, which the
 * adapter reports to the agent as a failure it can re-ask for. Add a bounded
 * retry here if real streams turn out to drop often.
 */
export const streamDatasetChunks = async (
  options: StreamDatasetChunksOptions
): Promise<StreamDatasetChunksResult> => {
  try {
    return await streamOnce(options);
  } catch (error) {
    if (!(error instanceof UnauthorizedStreamError)) throw error;

    await refreshAccessToken();

    try {
      return await streamOnce(options);
    } catch (retryError) {
      if (retryError instanceof UnauthorizedStreamError) await doLogout();
      throw retryError;
    }
  }
};
